import { SyntheticEvent, useEffect, useState } from "react";
import { Card, CardContent, Box, Tabs, Tab } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import useTaskInfo from "./use-task-info";
import TaskInfoFilters from "./task-info-filters";
import { useTaskInfoFilters } from "./use-task-info-filters";
import TaskInfoGrid from "./task-info-grid";
import useLongRunningTasks from "./use-long-running-tasks";
import LongRunningTasksStatus from "./long-running-tasks-status";

const LONG_RUNNING_TASKS_TAB = "long-running";

const getCurrentTabFromSearchParams = (searchParams: URLSearchParams) => {
  return searchParams.get("tab") === LONG_RUNNING_TASKS_TAB ? 1 : 0;
};

const TaskInfo = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState(() =>
    getCurrentTabFromSearchParams(searchParams),
  );
  const { filters, handleFilterChange, isAutoUpdating } = useTaskInfoFilters();
  const { data, isLoading, error, fetchTaskInfo } = useTaskInfo(filters);
  const {
    data: longRunningData,
    isLoading: isLongRunningLoading,
    error: longRunningError,
    mutate: refreshLongRunningTasks,
  } = useLongRunningTasks();

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);

    const nextSearchParams = new URLSearchParams(searchParams);
    if (newValue === 1) {
      nextSearchParams.set("tab", LONG_RUNNING_TASKS_TAB);
    } else {
      nextSearchParams.delete("tab");
    }
    setSearchParams(nextSearchParams);
  };

  useEffect(() => {
    setCurrentTab(getCurrentTabFromSearchParams(searchParams));
  }, [searchParams]);

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        height: "calc(100vh - 140px)",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        border: "1px solid",
        borderColor: "grey.200",
        background: "linear-gradient(to bottom, #ffffff, #fafafa)",
        transition: "box-shadow 0.3s ease-in-out",
        "&:hover": {
          boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
        },
      }}
      data-testid="task-info"
    >
      <CardContent
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="task tabs"
            sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Detection tasks" id="task-tab-0" />
            <Tab label="Long running tasks" id="task-tab-1" />
          </Tabs>
          {currentTab === 0 && (
            <TaskInfoFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClick={fetchTaskInfo}
              isAutoUpdating={isAutoUpdating}
            />
          )}
        </Box>

        <Box sx={{ flex: 1, minHeight: 0 }}>
          {currentTab === 0 && (
            <TaskInfoGrid
              data={data}
              isLoading={isLoading}
              error={error}
              onComplete={fetchTaskInfo}
            />
          )}
          {currentTab === 1 && (
            <LongRunningTasksStatus
              data={longRunningData}
              isLoading={isLongRunningLoading}
              error={longRunningError}
              onComplete={refreshLongRunningTasks}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default TaskInfo;
